import { memo, type SVGProps } from 'react';

export type IconSize = 'xs' | 'm';

/**
 * Glyph descriptor for an `Icon`. Encodes one or more Tractie SVG paths.
 *
 * The `path` holds the raw `d` attribute of each `<path>` of the source
 * Tractie icon (medium 24×24 / extra small 16×16 viewBox), in source order. Glyph instances are constants
 * and free of React-specific concerns so individual icon files can stay
 * trivial wrappers around the Tractie source data.
 */
export interface IconGlyph {
  /**
   * Optional `fillRule`, applied to every path. Tractie sometimes uses
   * `evenodd`. Defaults to `nonzero`.
   */
  fillRule?: 'evenodd' | 'nonzero';
  /** Stable token name (matches Tractie filename, e.g. `search`, `arrow-right`). */
  name: string;
  /**
   * Raw `d` attribute of the SVG path, or one per path for a glyph whose
   * source has several. Each value renders as its own `<path>`, so overlapping
   * subpaths fill as they do in the source.
   */
  path: string | readonly string[];
  /**
   * The glyph to show while the item that carries this glyph is the current
   * one, the Tractie `-thick` variant.
   */
  selected?: IconGlyph;
  /** Source viewBox dimensions — defaults to 24 (medium). */
  viewBox?: 16 | 24;
}

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'children'> {
  /**
   * Accessible name. When omitted the icon is rendered as decorative
   * (`aria-hidden="true"`).
   */
  'aria-label'?: string;
  /** Icon glyph data (typically imported from `./glyphs/*`). */
  glyph: IconGlyph;
  /**
   * Render size in pixels. Number = exact pixel size. Token = canonical Tractie size.
   * Defaults to `m` (24px).
   */
  size?: IconSize | number;
}

const TOKEN_SIZES: Record<IconSize, number> = { m: 24, xs: 16 };

/**
 * NS Tractie icon component.
 *
 * Renders a Tractie glyph as an inline `<svg>` using `currentColor`,
 * so the surrounding text/icon color cascades through (matching Tractie's
 * own conventions).
 *
 * Usage:
 * ```tsx
 * import { Icon } from '@ns/plugin-ns-theme';
 * import { iconSearch } from '@ns/plugin-ns-theme/icons/glyphs/search';
 *
 * <Icon glyph={iconSearch} aria-label="Zoeken" />
 * ```
 */
export const Icon = memo(function Icon({
  'aria-label': ariaLabel,
  glyph,
  size = 'm',
  ...rest
}: IconProps) {
  const pixelSize = typeof size === 'number' ? size : TOKEN_SIZES[size];
  const viewBox = glyph.viewBox ?? 24;
  const fillRule = glyph.fillRule ?? 'nonzero';
  const paths = typeof glyph.path === 'string' ? [glyph.path] : glyph.path;
  const a11y = ariaLabel
    ? { 'aria-label': ariaLabel, role: 'img' as const }
    : { 'aria-hidden': true as const };

  return (
    <svg
      {...rest}
      {...a11y}
      data-ns-icon={glyph.name}
      fill="currentColor"
      height={pixelSize}
      viewBox={`0 0 ${viewBox} ${viewBox}`}
      width={pixelSize}
      xmlns="http://www.w3.org/2000/svg"
    >
      {paths.map(pathData => (
        <path key={pathData} d={pathData} fillRule={fillRule} />
      ))}
    </svg>
  );
});
